import { useEffect, useState } from 'react';
import { getTokenFromStorage } from '@/utils/auth';
import { getReferenceMarkets } from '@/api/beatlyApi';
import { MARKETS_FALLBACK, mergeMarkets, type Markets } from '@/utils/markets';

/**
 * One fetch per page load, shared by every picker. No react-query on purpose: the pickers sit in cowork screens that
 * render (and are tested) without a QueryClientProvider.
 */
let cached: Markets | null = null;
let inflight: Promise<Markets> | null = null;
/** A failed call (an api-v2 without the route answers 404) is not retried until the page reloads; the snapshot serves. */
let failed = false;

function loadMarkets(token: string): Promise<Markets> {
  if (cached) return Promise.resolve(cached);
  if (failed) return Promise.resolve(MARKETS_FALLBACK);
  inflight ??= getReferenceMarkets(token)
    .then((live) => (cached = mergeMarkets(live)))
    .catch(() => { failed = true; return MARKETS_FALLBACK; })
    .finally(() => { inflight = null; });
  return inflight;
}

/** Test hook: forget the fetched list. */
export function resetMarketsCache(): void {
  cached = null;
  inflight = null;
  failed = false;
}

/**
 * The market list (support #238): GET /app/v2/reference/markets laid over the bundled snapshot. Always returns a full
 * list: the snapshot while loading, when signed out, or when the call fails.
 */
export function useMarkets(): Markets {
  const [markets, setMarkets] = useState<Markets>(cached ?? MARKETS_FALLBACK);
  useEffect(() => {
    const token = getTokenFromStorage();
    if (!token || cached || failed) return;
    let live = true;
    void loadMarkets(token).then((m) => { if (live) setMarkets(m); });
    return () => { live = false; };
  }, []);
  return markets;
}
